import { useEffect, useMemo, useState } from 'react';
import { calculateTotals, getFormActionButtons, getFormTitle, transformFormData } from '../../utils';

import { AritoForm, AritoHeaderTabs } from '@/components/custom/erp';

import { formSchema, initialFormValues } from '../../schemas';
import { useRepeatRowsHandler } from '@/lib/table-handlers';
import { useDetailRows, useTaxRows } from './hooks';
import { useAuth } from '@/contexts/auth-context';
import { useFormFieldState } from '../../hooks';
import { BasicInfoTab } from './BasicInfoTab';
import InfoDropdown from './InfoDropdown';
import { HistoryTab } from './HistoryTab';
import { FormMode } from '@/types/form';
import { DetailTab } from './DetailTab';
import { BottomBar } from './BottomBar';
import { TaxsTab } from './TaxsTab';

interface FormDialogProps {
  formMode: FormMode;
  open?: boolean;
  initialData?: any;
  onSubmit?: (data: any) => void;
  onClose: () => void;
  onAdd?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onCopy?: () => void;
}

export const FormDialog = ({
  open,
  formMode,
  initialData,
  onClose,
  onSubmit,
  onAdd,
  onEdit,
  onDelete,
  onCopy
}: FormDialogProps) => {
  const [isConfirm, setIsConfirm] = useState<boolean>(false);
  const [isFormDirty, setIsFormDirty] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>('info');
  const {
    rows: detailRows,
    setRows: setDetailRows,
    selectedRowUuid: detailSelectedRowUuid,
    selectedCell: detailSelectedCell,
    handleRowClick: detailHandleRowClick,
    handleCellClick: detailHandleCellClick,
    handleAddRow: detailHandleAddRow,
    handleDeleteRow: detailHandleDeleteRow,
    handleCopyRow: detailHandleCopyRow,
    handlePasteRow: detailHandlePasteRow,
    handleMoveRow: detailHandleMoveRow,
    handleCellValueChange: detailHandleCellValueChange,
    setNonWritableFields: setDetailNonWritableFields,
    setPasteConfigs: setDetailPasteConfigs
  } = useDetailRows(initialData?.chi_tiet_data || []);
  const {
    rows: taxRows,
    setRows: setTaxRows,
    selectedRowUuid: taxSelectedRowUuid,
    selectedCell: taxSelectedCell,
    handleRowClick: taxHandleRowClick,
    handleCellClick: taxHandleCellClick,
    handleAddRow: taxHandleAddRow,
    handleDeleteRow: taxHandleDeleteRow,
    handleCopyRow: taxHandleCopyRow,
    handlePasteRow: taxHandlePasteRow,
    handleMoveRow: taxHandleMoveRow,
    handleCellValueChange: taxHandleCellValueChange,
    setNonWritableFields: setTaxNonWritableFields,
    setPasteConfigs: setTaxPasteConfigs
  } = useTaxRows(initialData?.thue_data || []);
  const { state, actions } = useFormFieldState(initialData);
  const { entityUnit } = useAuth();
  const handleRepeatDetailRows = useRepeatRowsHandler({
    setRows: setDetailRows,
    markDirty: () => setIsFormDirty(true)
  });
  const handleRepeatTaxRows = useRepeatRowsHandler({
    setRows: setTaxRows,
    markDirty: () => setIsFormDirty(true)
  });

  useEffect(() => {
    if (!open) {
      setIsFormDirty(false);
      setActiveTab('info');
    }
  }, [open]);

  const { t_ps_no_nt, t_ps_co_nt } = useMemo(() => {
    return calculateTotals(detailRows);
  }, [detailRows]);

  const handleSubmit = (data: any) => {
    const totals = { t_ps_no_nt, t_ps_co_nt };
    const formData = transformFormData(data, state, entityUnit, detailRows, taxRows, totals, formMode);

    onSubmit?.(formData);
    setIsFormDirty(false);
  };

  const handleClose = () => {
    if (isFormDirty) {
      setIsConfirm(true);
    } else {
      onClose();
    }
  };

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
  };

  const title = getFormTitle(formMode, activeTab);
  const actionButtons = getFormActionButtons(formMode, activeTab, {
    onAdd,
    onEdit,
    onDelete,
    onCopy,
    handleClose
  });

  return (
    <>
      <AritoForm
        mode={formMode}
        title={title}
        initialData={initialData || initialFormValues}
        actionButtons={actionButtons}
        subTitle='Phiếu kế toán'
        onSubmit={handleSubmit}
        onClose={handleClose}
        schema={formSchema}
        headerFields={
          <div onChange={() => setIsFormDirty(true)}>
            <AritoHeaderTabs
              tabs={[
                {
                  id: 'info',
                  label: 'Thông tin',
                  component: <BasicInfoTab formMode={formMode} formState={{ state, actions }} />
                },
                ...(formMode === 'view'
                  ? [
                      {
                        id: 'history',
                        label: 'Lịch sử',
                        component: <HistoryTab formMode={formMode} />
                      }
                    ]
                  : [])
              ]}
              onTabChange={handleTabChange}
              defaultTabIndex={activeTab === 'info' ? 0 : activeTab === 'history' ? 1 : 0}
              actionButtons={
                formMode !== 'add' && <InfoDropdown uuid={initialData?.uuid} ma_ct={initialData?.chung_tu} />
              }
            />
          </div>
        }
        tabs={
          activeTab === 'info' && [
            {
              id: 'details',
              label: 'Chi tiết',
              component: (
                <DetailTab
                  formMode={formMode}
                  rows={detailRows}
                  selectedRowUuid={detailSelectedRowUuid}
                  selectedCell={detailSelectedCell}
                  onRowClick={detailHandleRowClick}
                  onCellClick={detailHandleCellClick}
                  onAddRow={detailHandleAddRow}
                  onDeleteRow={detailHandleDeleteRow}
                  onCopyRow={detailHandleCopyRow}
                  onPasteRow={detailHandlePasteRow}
                  onMoveRow={detailHandleMoveRow}
                  onRepeatForRows={handleRepeatDetailRows}
                  onCellValueChange={detailHandleCellValueChange}
                  handleUpdateNonWritableFields={setDetailNonWritableFields}
                  handleUpdatePasteConfigs={setDetailPasteConfigs}
                />
              )
            },
            {
              id: 'tax',
              label: 'Thuế',
              component: (
                <TaxsTab
                  formMode={formMode}
                  rows={taxRows}
                  selectedRowUuid={taxSelectedRowUuid}
                  selectedCell={taxSelectedCell}
                  onRowClick={taxHandleRowClick}
                  onCellClick={taxHandleCellClick}
                  onAddRow={taxHandleAddRow}
                  onDeleteRow={taxHandleDeleteRow}
                  onCopyRow={taxHandleCopyRow}
                  onPasteRow={taxHandlePasteRow}
                  onMoveRow={taxHandleMoveRow}
                  onRepeatForRows={handleRepeatTaxRows}
                  onCellValueChange={taxHandleCellValueChange}
                  handleUpdateNonWritableFields={setTaxNonWritableFields}
                  handleUpdatePasteConfigs={setTaxPasteConfigs}
                />
              )
            }
          ]
        }
        bottomBar={
          activeTab === 'info' && (
            <BottomBar formMode={formMode as 'view' | 'add' | 'edit'} t_ps_no_nt={t_ps_no_nt} t_ps_co_nt={t_ps_co_nt} />
          )
        }
      />
    </>
  );
};
